import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

function LucideChevronLeft(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function LucideChevronRight(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function pickBreakpoint(breakpoints, width) {
  const sorted = [...breakpoints].sort((a, b) => a.minWidth - b.minWidth);
  let chosen = sorted[0];
  for (let i = 0; i < sorted.length; i += 1) {
    if (width >= sorted[i].minWidth) chosen = sorted[i];
  }
  return chosen;
}

function OiCarousel({
  items,
  renderItem,
  getItemKey,
  breakpoints,
  gap,
  ariaLabel,
  className,
  prevButtonId,
  nextButtonId,
}) {
  const viewportRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [scrollState, setScrollState] = useState({ atStart: true, atEnd: false });

  const activeBp = useMemo(() => {
    if (!breakpoints.length) return null;
    if (viewportWidth <= 0) return breakpoints[0];
    return pickBreakpoint(breakpoints, viewportWidth);
  }, [breakpoints, viewportWidth]);

  const slidesPerView = activeBp ? activeBp.slidesPerView : 1;
  const itemWidthPx =
    viewportWidth > 0
      ? (viewportWidth - gap * (slidesPerView - 1)) / slidesPerView
      : 0;

  const updateScrollState = useCallback((el) => {
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    setScrollState((prev) =>
      prev.atStart === atStart && prev.atEnd === atEnd ? prev : { atStart, atEnd }
    );
  }, []);

  const measure = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    setViewportWidth(el.clientWidth);
    updateScrollState(el);
  }, [updateScrollState]);

  useLayoutEffect(() => {
    measure();
  }, [measure, items.length]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    const el = viewportRef.current;
    let ro;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro && el) ro.disconnect();
    };
  }, [measure]);

  const scrollStep = itemWidthPx > 0 ? itemWidthPx + gap : 0;

  const scrollPrev = () => {
    const el = viewportRef.current;
    if (!el || scrollStep <= 0) return;
    el.scrollBy({ left: -scrollStep, behavior: 'smooth' });
  };

  const scrollNext = () => {
    const el = viewportRef.current;
    if (!el || scrollStep <= 0) return;
    el.scrollBy({ left: scrollStep, behavior: 'smooth' });
  };

  const onScroll = () => {
    const el = viewportRef.current;
    if (el) updateScrollState(el);
  };

  const defaultKey = (item, index) =>
    item != null && item.id != null ? `oi-carousel-${item.id}` : `oi-carousel-${index}`;
  const keyFn = getItemKey || defaultKey;

  const rootClass = ['oi-carousel', className].filter(Boolean).join(' ');

  return (
    <div
      className={rootClass}
      role="region"
      aria-label={ariaLabel}
      aria-roledescription="carrusel"
    >
      <button
        type="button"
        id={prevButtonId || undefined}
        className="oi-carousel__button oi-carousel__button--prev"
        aria-label="Anterior"
        onClick={scrollPrev}
        disabled={scrollState.atStart}
      >
        <LucideChevronLeft />
      </button>
      <button
        type="button"
        id={nextButtonId || undefined}
        className="oi-carousel__button oi-carousel__button--next"
        aria-label="Siguiente"
        onClick={scrollNext}
        disabled={scrollState.atEnd}
      >
        <LucideChevronRight />
      </button>
      <div
        className="oi-carousel__viewport"
        ref={viewportRef}
        onScroll={onScroll}
      >
        <div className="oi-carousel__track" style={{ gap: `${gap}px` }}>
          {items.map((item, index) => (
            <div
              key={keyFn(item, index)}
              className="oi-carousel__item"
              style={
                itemWidthPx > 0
                  ? { flex: `0 0 ${itemWidthPx}px`, width: `${itemWidthPx}px` }
                  : { flex: '0 0 auto' }
              }
            >
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OiCarousel;
